import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LiveBadge, NetworkPill, OfferTypeChip, SyncStatusPill, DeeplinkBadge, CatalogStatusPill } from "./BrandBadges";
import BrandRowActions from "./BrandRowActions";
import { effectiveClickCap } from "@/lib/trafficRules";

/**
 * Catalog table — one row per brand. All inline-edit + row-action
 * state lives in the parent (AdminBrands) so that drawers, refetches,
 * and toasts stay co-located with the data-fetch logic.
 *
 * Two inline editors:
 *   • Commission % — click the value, type a new %, Save.
 *   • GEO          — click the chips, type comma-separated ISO-2
 *                    codes, Save (or Enter / Escape).
 *
 * Sync-status pill (Manual / Feed+Overrides) and offer-type chip
 * (CPA/CPC) sit alongside the network pill in the Network column —
 * lets admins see provenance + payout shape at a single glance.
 */
export default function BrandsTable({
  brands, loading,
  hasDrift,
  // Commission inline editor
  editingId, editValue, setEditingId, setEditValue, onSaveCommission,
  // GEO inline editor
  geoEditingId, geoEditValue, setGeoEditingId, setGeoEditValue, geoSaving, onSaveGeo,
  // Row actions
  onToggleOfferType, onTrafficRules, onRefPolicy,
  onContract, onEditManual, onAuditManual, onDeleteManual,
}) {
  if (loading) {
    return <div className="text-sm text-slate-400 py-8">Loading brands...</div>;
  }

  const thClass = "text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap align-middle";
  const tdClass = "px-4 py-3 align-middle";

  return (
    <div className="border border-slate-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm min-w-[1080px]">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className={`${thClass} min-w-[120px]`}>Brand</th>
            <th className={`${thClass} min-w-[100px]`}>Advertiser</th>
            <th className={`${thClass} w-[200px] max-w-[200px]`}>Campaign ID</th>
            <th className={`${thClass}`}>Status</th>
            <th className={`${thClass} text-right`}>Payout / Base Commission %</th>
            <th className={`${thClass}`}>GEO</th>
            <th className={`${thClass} min-w-[180px]`}>Network</th>
            <th className={`${thClass}`}>Traffic Rules</th>
            <th className={`${thClass} text-right sticky right-0 z-10 bg-slate-50 min-w-[148px] shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)]`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {brands.map((b) => {
            // Shapes whose real rate lives in flat_cpa_amount, not
            // base_commission_pct (which is always 0 for these by
            // design) — "flat" (per-lead/per-sale CPA) and "tiered"
            // (a single-number Skimlinks payout inside a $X–$Y range,
            // same underlying field). Confirmed: 100% of "tiered" rows
            // showed the same misleading "/0%" as "flat" did before
            // this was fixed.
            const isFlatLike = b.payout_shape === "flat" || b.payout_shape === "tiered";
            return (
            <tr key={b.campaign_id} className="group border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`brand-row-${b.campaign_id}`}>
              <td className={`${tdClass} font-medium text-slate-900 max-w-[180px]`}>
                <span className="block truncate" title={b.name}>{b.name}</span>
              </td>
              <td className={`${tdClass} text-slate-600 max-w-[140px]`}>
                <span className="block truncate" title={b.advertiser_name}>{b.advertiser_name}</span>
              </td>
              <td className={`${tdClass} w-[200px] max-w-[200px]`}>
                <span className="block font-mono text-xs text-slate-500 truncate" title={b.campaign_id}>{b.campaign_id}</span>
              </td>
              <td className={tdClass}>
                <CatalogStatusPill brand={b} />
              </td>
              <td className={`${tdClass} text-right whitespace-nowrap`}>
                {editingId === b.campaign_id ? (
                  <div className="flex items-center justify-end gap-2">
                    {isFlatLike && (
                      <span className="text-xs text-slate-400 font-mono">$</span>
                    )}
                    <Input
                      type="number"
                      step="0.1"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-20 h-8 text-right font-mono text-sm"
                      data-testid="edit-commission-input"
                    />
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onSaveCommission(b.campaign_id, isFlatLike)} data-testid="save-commission-btn">Save</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <span
                    className="font-mono cursor-pointer hover:text-[#002FA7] transition-colors inline-flex items-baseline justify-end gap-1"
                    onClick={() => {
                      setEditingId(b.campaign_id);
                      setEditValue(
                        isFlatLike
                          ? (b.flat_cpa_amount ?? 0).toString()
                          : (b.base_commission_pct ?? 0).toString(),
                      );
                    }}
                    data-testid={`commission-value-${b.campaign_id}`}
                    title={
                      isFlatLike
                        ? "Click to edit the flat per-lead/per-sale rate. Feed payout display shown before the slash."
                        : "Click to edit base commission %. Feed payout display shown before the slash."
                    }
                  >
                    {b.payout_display && (
                      <span className="text-[11px] text-slate-400" data-testid={`payout-display-${b.campaign_id}`}>
                        {b.payout_display} /
                      </span>
                    )}
                    {/* Flat-rate (per-lead) and tiered brands always carry
                        base_commission_pct=0 by design — the real rate
                        lives in flat_cpa_amount. Showing "0%" next to a
                        "$X per lead" feed rate reads as a misconfigured
                        commission rather than "this offer isn't
                        percentage-based at all". */}
                    {isFlatLike ? (
                      <span>${(b.flat_cpa_amount || 0).toFixed(2)}</span>
                    ) : (
                      <span>{b.base_commission_pct || 0}%</span>
                    )}
                  </span>
                )}
              </td>
              <td className={tdClass} data-testid={`geo-cell-${b.campaign_id}`}>
                {geoEditingId === b.campaign_id ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      autoFocus
                      value={geoEditValue}
                      onChange={(e) => setGeoEditValue(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveGeo(b.campaign_id);
                        if (e.key === "Escape") setGeoEditingId(null);
                      }}
                      placeholder="US, CA, GB"
                      className="w-40 h-7 text-xs font-mono"
                      data-testid={`geo-input-${b.campaign_id}`}
                    />
                    <Button size="sm" variant="outline" className="h-7 text-xs px-2" disabled={geoSaving} onClick={() => onSaveGeo(b.campaign_id)} data-testid={`geo-save-${b.campaign_id}`}>
                      {geoSaving ? "…" : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setGeoEditingId(null)}>×</Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setGeoEditingId(b.campaign_id);
                      setGeoEditValue((b.geo_locations || []).join(", "));
                    }}
                    className="flex items-center gap-1 text-left hover:bg-slate-100 px-1.5 py-0.5 rounded transition-colors"
                    title="Click to edit. ISO-3166-1 alpha-2 codes (e.g. US, CA, GB)."
                    data-testid={`geo-edit-${b.campaign_id}`}
                  >
                    {(b.geo_locations && b.geo_locations.length > 0) ? (
                      <>
                        {b.geo_locations.slice(0, 3).map((cc) => (
                          <span
                            key={cc}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-sky-50 text-sky-700 border border-sky-200"
                          >
                            {cc}
                          </span>
                        ))}
                        {b.geo_locations.length > 3 && (
                          <span className="text-[10px] text-slate-500 font-mono ml-0.5">
                            +{b.geo_locations.length - 3}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">add geo</span>
                    )}
                  </button>
                )}
              </td>
              <td className={tdClass}>
                <div className="flex items-center flex-nowrap gap-1.5">
                  <NetworkPill network={b.network} />
                  <OfferTypeChip brand={b} onToggle={(next) => onToggleOfferType(b, next)} />
                  <SyncStatusPill brand={b} />
                  <DeeplinkBadge brand={b} />
                  <LiveBadge brand={b} />
                </div>
              </td>
              <td className={`${tdClass} whitespace-nowrap`} data-testid={`traffic-rules-cell-${b.campaign_id}`}>
                {(() => {
                  const capInfo = effectiveClickCap(
                    b.click_cap_per_day_effective ?? b.click_cap_per_day,
                  );
                  const types = b.allowed_traffic_types || [];
                  const restricted = types.filter((t) => !t.allowed).length;
                  // MaxBounty's own channel restrictions (a DIFFERENT axis
                  // than the enforcement matrix above — see
                  // network_traffic_notes doc in services/maxbounty.py).
                  // Surfaced here so it's scannable across rows without
                  // opening the drawer for every brand.
                  const netTypes = b.network_traffic_notes?.allowed_traffic_types || {};
                  const netRestricted = Object.values(netTypes).filter(
                    (v) => String(v).toUpperCase() === "N"
                  ).length;
                  return (
                    <button
                      type="button"
                      onClick={() => onTrafficRules && onTrafficRules(b)}
                      className="text-left text-xs leading-snug hover:bg-slate-50 rounded px-1.5 py-1 -mx-1.5 transition-colors"
                      data-testid={`open-traffic-rules-${b.campaign_id}`}
                      title="Edit click cap + allowed traffic types"
                    >
                      <div className="font-mono text-slate-700">
                        {capInfo.label}
                        {capInfo.isDefault ? " (default)" : ""}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {restricted === 0
                          ? "all types allowed"
                          : `${restricted} of ${types.length || 11} restricted`}
                      </div>
                      {netRestricted > 0 && (
                        <div
                          className="text-[10px] text-amber-700 font-semibold mt-0.5"
                          data-testid={`network-restrictions-${b.campaign_id}`}
                        >
                          ⚠ {netRestricted} network restriction{netRestricted === 1 ? "" : "s"}
                        </div>
                      )}
                    </button>
                  );
                })()}
              </td>
              <td className={`${tdClass} text-right sticky right-0 z-10 bg-white group-hover:bg-slate-50 min-w-[148px] shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)]`}>
                <BrandRowActions
                  brand={b}
                  hasDrift={hasDrift(b.campaign_id)}
                  onContract={onContract}
                  onEdit={onEditManual}
                  onAudit={onAuditManual}
                  onRefPolicy={onRefPolicy}
                  onDelete={onDeleteManual}
                />
              </td>
            </tr>
            );
          })}
          {brands.length === 0 && (
            <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400 text-sm">No brands found. Use the per-network "Sync now" or "Lookup" buttons above to pull from a feed partner.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
